import React, { useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, compose } from 'redux';

import './ExchangeSteps.scss';
import CheckBox from 'src/components/Shared/Checkbox/Checkbox';
import ExchangeFromForm from '../ExchangeFromForm/ExchangeFromForm';
import ExchangeFromSummary from '../ExchangeFromSummary/ExchangeFromSummary';
import ExchangeReview from '../ExchangeReview/ExchangeReview';
import ExchangeToForm from '../ExchangeToForm/ExchangeToForm';
import ExchangeToSummary from '../ExchangeToSummary/ExchangeToSummary';
import ProgressStep from '../../ProgressStep/ProgressStep';
import { moduleActions } from '../../../redux/modules';
import {
  isCustomerWebViewEnabled,
  isSalesforceRep,
  isEmbeddedInIFrame,
} from '../../../redux/modules/authentication/authentication.module.selectors';
import {
  getAcceptedRI2020WithholdingDisclosureApplied,
  getExchangeContributionYear,
  getExchangeFromIsValid,
  getExchangeFromSummaryFundList,
  getExchangeToIsValid,
  getExchangeToSummaryFundList,
  getExchangeType,
  getExchangeIsTaxable,
} from '../../../redux/modules/exchangeTransaction/exchangeTransaction.selectors';
import { getDefaultBankAgreementKey } from '../../../redux/modules/existingBanks/existingBanks.selectors';
import { getOnlyOWNRRMDs } from '../../../redux/modules/RMD/RMDSetting.selectors';
import { getAutoRMDImpacts } from 'src/redux/modules/shoppingCart/shoppingCart.selectors';
import {
  CANCEL_TRADE_CONFIRMATION_MODAL,
  DISTRIBUTION_TRANSACTIONS,
  IRA_WITHHOLDING_WARNING_MODAL,
} from '../../../redux/modules/modals/modals.module';
import { getMandatoryWithholdingStates } from 'src/redux/modules/stateWithholding/selectors';
import AppTitle from '../../Shared/AppTitle/AppTitle';
import ErrorSlideIn from '../../Shared/ErrorSlideIn/ErrorSlideIn';
import { scrollElementIntoFullView } from '../../../utils/dom-utils';
import { hasValue } from '../../../utils/utils';
import { setRegistrationTypes } from '../../../redux/modules/user/UserRegistrationTypes/UserRegistrationTypes.module';
import If from '../../Shared/If/If';
import { FederalTaxPercent, AGREEMENT_TYPES } from 'src/settings/settings';

import { getHasAPP } from 'src/redux/modules/productTypes/productType.selectors';
import { getExchangeTradeDatesObject } from '../../../utils/transactions-util';

const ownsRMDEligble = (onlyOWNRRMDs, autoRMDImpacts) => {
  // check that owns auto rmd
  // check that has exchange/sell that are RMD eligible
  if (onlyOWNRRMDs.length !== 0 && autoRMDImpacts.length !== 0) {
    return true;
  }
  return false;
};

const exchangeTradToNonRetire = (
  exchangeFromSummaryFundList,
  exchangeToSummaryFundList
) => {
  // check that the user is transferrig from retirement RMD to non-retirement
  if (
    exchangeFromSummaryFundList[0].displayClassification === 'RETIREMENT' &&
    exchangeToSummaryFundList[0].displayClassification === 'NON_RETIREMENT'
  ) {
    return true;
  }
  return false;
};

const pickSelectedRegistrations = (registrations, selectedFundList) => {
  if (!hasValue(selectedFundList) || !hasValue(registrations)) {
    return [];
  }

  const firstSelectedFund = selectedFundList[0]; // In MF Exchange only the first fund is used to generate the registration
  const matchingKey = `${
    firstSelectedFund.agreementID
  }-${firstSelectedFund.agreementNumber.replace(/-\d/, '')}`;
  return registrations.find((reg) =>
    reg.modelAgreementUniqueIDs.some((id) => id.endsWith(matchingKey))
  );
};

const ExchangeSteps = ({
  acceptedRI2020WithholdingDisclosureApplied,
  autoRMDImpacts,
  personalData,
  actions: {
    openModal,
    selectExchangeToFunds,
    setProgress,
    submitExchange,
    validateExchange,
    resetTransaction,
  },
  currentStep,
  isIFrame,
  isSalesforceRep,
  customerWebViewEnabled,
  defaultBankAgreementKey,
  exchangeContributionYear,
  exchangeFederalWithholdingApplied,
  exchangeFederalWithholdingPercent,
  exchangeFromIsValid,
  exchangeFromSummaryFundList,
  existingRMD,
  exchangeToIsValid,
  exchangeToSummaryFundList,
  exchangeType,
  exchangeIsTaxable,
  executeExchange,
  hasAPP,
  isLoading,
  isValid,
  isValidated,
  isValidating,
  userRegistrationTypeInfo,
  validationMessages,
  eligibleForRMD,
  mandatoryWithholdingStates,
  testNextBtnClicked = () => {},
  inTest,
}) => {
  const [isRepSelected, setIsRepSelected] = useState(false);
  const editingExchangeFederalInError =
    exchangeFederalWithholdingPercent !== 0 &&
    (exchangeFederalWithholdingPercent < FederalTaxPercent.MIN ||
      exchangeFederalWithholdingPercent > FederalTaxPercent.MAX);

  // hook - to be marked once the modal has been shown once to warn
  const [RMDImpactsDisplayed, setAutoRMDModalDisplayed] = useState(false);

  const isCustomerViewEnabledAndIsSalesForceRep = () => {
    return !customerWebViewEnabled && isSalesforceRep;
  };
  return (
    <form onSubmit={(e) => e.preventDefault()} className="animated fadeInUp">
      <If test={!(isIFrame || isCustomerViewEnabledAndIsSalesForceRep())}>
        <AppTitle
          title={hasAPP ? 'Exchange Accounts' : 'Exchange Mutual Funds'}
          subtitle="Move assets from one eligible investment to another."
        />
      </If>
      <ProgressStep
        title={
          <span>
            <span className="exchange-steps-title-bold">From: </span>Select the
            investment you want to exchange out of
          </span>
        }
        className="exchange-from-step step1"
        step={1}
        active={currentStep === 1}
        complete={currentStep > 1}
        expanded
        nextButtonLabel={
          <span>
            <b>NEXT: </b>Select{' '}
            <span className="exchange-steps-button-exchange-to">
              Exchange To
            </span>{' '}
            Investment
          </span>
        }
        nextButtonDisabled={!exchangeFromIsValid}
        onNextClick={() => {
          selectExchangeToFunds(exchangeFromSummaryFundList);
          setRegistrationTypes(
            pickSelectedRegistrations(
              userRegistrationTypeInfo.registrationTypes,
              exchangeFromSummaryFundList
            )
          );
          setProgress(2);
        }}
        showCancelLink={!isCustomerViewEnabledAndIsSalesForceRep()}
        onCancelClick={() => openModal(CANCEL_TRADE_CONFIRMATION_MODAL)}
      >
        {currentStep === 1 ? (
          <ExchangeFromForm
            isSalesforceRep={isSalesforceRep}
            customerWebViewEnabled={customerWebViewEnabled}
          />
        ) : (
          <ExchangeFromSummary />
        )}
      </ProgressStep>

      <ProgressStep
        title={
          <span>
            <span className="exchange-steps-title-bold">To: </span>Select the
            investment you want to exchange into
          </span>
        }
        className="exchange-to-step step2"
        step={2}
        active={currentStep === 2}
        complete={currentStep > 2}
        expanded
        nextButtonClassName="e2e-step2-verify-submit"
        nextButtonLabel={
          <span>
            <b>NEXT:</b> Review & Submit
          </span>
        }
        nextButtonDisabled={(!exchangeToIsValid || isValidating) && !inTest}
        nextButtonLoading={isValidating}
        onNextClick={() => {
          testNextBtnClicked();
          exchangeIsTaxable &&
            openModal(IRA_WITHHOLDING_WARNING_MODAL, {
              showPageIraTable: true,
              isRoth: exchangeFromSummaryFundList
                .map((fund) => fund.agreementType)
                .some(
                  (fund) =>
                    fund === AGREEMENT_TYPES.ROTH_IRA ||
                    fund === AGREEMENT_TYPES.ROTH_ROLLOVER_IRA
                ),
              userState: exchangeFromSummaryFundList[0]?.address?.state,
              mandatoryWithholdingStates: mandatoryWithholdingStates,
            });

          !inTest &&
            validateExchange(
              exchangeContributionYear,
              exchangeFederalWithholdingApplied,
              exchangeFederalWithholdingPercent,
              exchangeFromSummaryFundList,
              exchangeToSummaryFundList,
              exchangeType
            ).then(({ body = {} }) => {
              if (body.valid) {
                const ownsRMD = ownsRMDEligble(existingRMD, autoRMDImpacts);
                const exchangeTrad = exchangeTradToNonRetire(
                  exchangeFromSummaryFundList,
                  exchangeToSummaryFundList
                );
                if (ownsRMD && exchangeTrad && !RMDImpactsDisplayed) {
                  setAutoRMDModalDisplayed(true);
                  openModal(DISTRIBUTION_TRANSACTIONS);
                  //setProgress(2);
                } else {
                  setProgress(3);
                }
              }
            });
        }}
        showCancelLink={!isCustomerViewEnabledAndIsSalesForceRep()}
        onCancelClick={() => openModal(CANCEL_TRADE_CONFIRMATION_MODAL)}
      >
        {currentStep === 2 ? (
          <ExchangeToForm
            isSalesforceRep={isSalesforceRep}
            customerWebViewEnabled={customerWebViewEnabled}
            fromFunds={exchangeFromSummaryFundList}
            selectedFund={exchangeFromSummaryFundList[0]}
            eligibleForRMD={eligibleForRMD}
            currentStep={currentStep}
            showAddNewFundOrAccount={exchangeFromSummaryFundList.length > 0}
          />
        ) : (
          <ExchangeToSummary />
        )}
      </ProgressStep>

      <ErrorSlideIn msg={validationMessages} show={isValidated && !isValid} />

      <ProgressStep
        title="Review your exchange"
        className="review-step step3"
        checkbox={
          currentStep === 3 &&
          exchangeIsTaxable &&
          !customerWebViewEnabled &&
          isSalesforceRep ? (
            <div className="sell-fund-availiblity-check-con">
              {' '}
              <CheckBox
                checked={isRepSelected}
                inline={true}
                onChange={(e) => setIsRepSelected(e)}
              />
              <span className="sell-fund-availiblity-check-text">
                I have read the current{' '}
                <a
                  href="https://troweprice.egain.cloud/kb/retail/content/202200000002595/IRA-Redemption-Disclosures"
                  rel="noreferrer"
                  target="_blank"
                >
                  Federal Withholding Disclosures
                </a>
                . Note: Disclosure must be read whether federal withholding
                elected or not
              </span>
            </div>
          ) : (
            <></>
          )
        }
        step={3}
        active={currentStep === 3}
        expanded={currentStep === 3}
        nextButtonLabel="SUBMIT"
        nextButtonDisabled={
          !acceptedRI2020WithholdingDisclosureApplied ||
          customerWebViewEnabled ||
          isLoading ||
          editingExchangeFederalInError ||
          (!isRepSelected &&
            !customerWebViewEnabled &&
            isSalesforceRep &&
            exchangeIsTaxable)
        }
        onNextClick={() => {
          scrollElementIntoFullView('exchange-page-container');
          submitExchange(
            exchangeContributionYear,
            exchangeFederalWithholdingApplied,
            exchangeFederalWithholdingPercent,
            exchangeFromSummaryFundList,
            exchangeToSummaryFundList,
            exchangeType,
            defaultBankAgreementKey
          );
          resetTransaction();
        }}
        showCancelLink={!isCustomerViewEnabledAndIsSalesForceRep()}
        onCancelClick={() => openModal(CANCEL_TRADE_CONFIRMATION_MODAL)}
      >
        {currentStep === 3 ? (
          <ExchangeReview
            personalData={personalData}
            exchangeTradeDates={getExchangeTradeDatesObject(executeExchange)}
          />
        ) : null}
      </ProgressStep>
    </form>
  );
};

const mapStateToProps = (state) => {
  const {
    executeExchange: {
      isLoading,
      isValid,
      isValidated,
      isValidating,
      validationMessages,
    },
    progress: { currentStep },
    userRegistrationTypeInfo,
    exchangeTransaction: {
      exchangeFederalWithholdingApplied,
      exchangeFederalWithholdingPercent,
    },
  } = state;

  return {
    acceptedRI2020WithholdingDisclosureApplied:
      getAcceptedRI2020WithholdingDisclosureApplied(state),
    autoRMDImpacts: getAutoRMDImpacts(state),
    defaultBankAgreementKey: getDefaultBankAgreementKey(state),
    currentStep,
    isIFrame: isEmbeddedInIFrame(state),
    isSalesforceRep: isSalesforceRep(state),
    exchangeIsTaxable: getExchangeIsTaxable(state),
    customerWebViewEnabled: isCustomerWebViewEnabled(state),
    exchangeContributionYear: getExchangeContributionYear(state),
    exchangeFederalWithholdingApplied,
    exchangeFederalWithholdingPercent,
    exchangeFromIsValid: getExchangeFromIsValid(state),
    exchangeFromSummaryFundList: getExchangeFromSummaryFundList(state),
    existingRMD: getOnlyOWNRRMDs(state),
    exchangeToIsValid: getExchangeToIsValid(state),
    exchangeToSummaryFundList: getExchangeToSummaryFundList(state),
    exchangeType: getExchangeType(state),
    hasAPP: getHasAPP(state),
    isLoading,
    isValid,
    isValidated,
    isValidating,
    userRegistrationTypeInfo,
    validationMessages,
    mandatoryWithholdingStates: getMandatoryWithholdingStates(state),
    executeExchange: state.executeExchange,
  };
};

const mapDispatchToProps = (dispatch) => ({
  actions: {
    ...bindActionCreators(moduleActions, dispatch),
    setProgress: (step) => {
      scrollElementIntoFullView(`step${step}`);
      dispatch(moduleActions.setProgress(step));
    },
    selectExchangeToFunds: (exchangeFromSummaryFundList) => {
      if (hasValue(exchangeFromSummaryFundList)) {
        dispatch(
          moduleActions.searchExchangeToExisting(exchangeFromSummaryFundList[0])
        );
      }
    },
  },
});

export default compose(connect(mapStateToProps, mapDispatchToProps))(
  ExchangeSteps
);
